import { useLayoutEffect, useMemo, useState } from 'react';
import { Producto } from './Producto';
import { Box, Text } from '@bpba/ui-components';
import { Filtros } from '../filtros/Filtros';
import { ModuleContainerGeneric } from '@bpba/modules-components';
import { ProductoData } from '../../model/ProductoData';
import { Titulo } from '../shared/Titulo';
import { StyledButton } from '../shared/StyledButton';
import { useDevice } from '@bpba/ui-components';
import { useUserStatus } from '../../config/authentication/useUserStatus';
import { SinProductos } from './SinProductos';
import { StyledModuleContainerGeneric } from '../shared/StyledModuleContainerGeneric';

/**
 * Cuántos productos se muestran en cada página, para cada resolución
 */
const PER_PAGE_LIST = [4, 9, 10];

type CatalogoComponentProps = {
  orden: number;
  data: ProductoData[];
  moduloTitulo?: string;
  hideFilters?: boolean;
  hidePage?: boolean;
  prodPorFilaList: number[];
  iconoRubro?: string;
  showRubroFilter?: boolean;
  rubroId?: string | undefined;
  legales?: string | undefined;
  isOnTop?: boolean;
};

/**
 * @param prodPorFilaList - Cuántos productos se muestran por fila, para cada resolución.
 */
export const CatalogoComponent = (props: CatalogoComponentProps) => {
  let [productosPorFila, setProductosPorFila] = useState(props.prodPorFilaList?.[props.prodPorFilaList.length - 1]);
  let [perPage, setPerPage] = useState(PER_PAGE_LIST[PER_PAGE_LIST.length - 1]);
  let [page, setPage] = useState(1);
  let [puntosDesde, setPuntosDesde] = useState(0);
  let [puntosHasta, setPuntosHasta] = useState(Number.MAX_VALUE);
  let [ordenPor, setOrdenPor] = useState('');
  const { isLoggedin } = useUserStatus();
  const device = useDevice();

  const selectConfig = () => {
    switch (device) {
      case 'mobile': {
        setPerPage(PER_PAGE_LIST[0]);
        setProductosPorFila(props.prodPorFilaList[0]);
        break;
      }
      case 'tablet': {
        setPerPage(PER_PAGE_LIST[1]);
        setProductosPorFila(props.prodPorFilaList[props.prodPorFilaList.length - 2]);
        break;
      }
      case 'desktop': {
        setPerPage(PER_PAGE_LIST[2]);
        setProductosPorFila(props.prodPorFilaList[props.prodPorFilaList.length - 1]);
        break;
      }
    }
  };

  const handleResize = () => {
    selectConfig();
  };

  useLayoutEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const { listProductos, totalPage, totalFiltered } = useMemo(() => {
    /**Filtrado*/
    const list = props.data?.filter((producto) => producto.puntos >= puntosDesde && producto.puntos < puntosHasta);
    const totalPage = list.length / perPage;

    /**Ordenamiento */
    if (ordenPor === 'desc') {
      list?.sort((a, b) => b.puntos - a.puntos);
    } else if (ordenPor === 'asc') {
      list?.sort((a, b) => a.puntos - b.puntos);
    }
    /**Paginación */
    const pagedList = list.slice(0, page * perPage);

    return {
      listProductos: pagedList,
      totalPage: totalPage,
      totalFiltered: list.length,
    };
  }, [props.data, puntosDesde, puntosHasta, page, perPage, ordenPor]);

  let items = listProductos.map((producto, index) => {
    return (
      <Producto
        key={producto.codigo}
        id={'producto_' + index}
        data={producto}
        width={['calc(calc(100% - ' + (productosPorFila - 1).toString() + ' * 16px)/' + productosPorFila.toString() + ')']}
        isLast={(index + 1) % productosPorFila === 0}
      />
    );
  });

  const focusElement = (id: string) => {
    setTimeout(() => {
      document.getElementById(id)?.focus();
    }, 500);
  };

  const onClick = () => {
    const id = page * perPage;
    setPage((current) => current + 1);
    focusElement('producto_' + id);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event && (event.code === 'Enter' || event.code === 'Space')) {
      onClick();
      event.stopPropagation();
      event.preventDefault();
    }
  };

  return (
    <>
      <StyledModuleContainerGeneric
        paddingBottom={['32px !important']}
        paddingTop={props.isOnTop && !isLoggedin ? ['0px !important'] : ['40px !important']}
      >
        <Titulo icono={props.iconoRubro}>{props.moduloTitulo}</Titulo>
      </StyledModuleContainerGeneric>
      <ModuleContainerGeneric marginBottom={0} paddingBottom={['64px']} paddingTop={'0px !important'}>
        {!props.hideFilters && (
          <Box>
            <Filtros
              setPuntosDesde={setPuntosDesde}
              setPuntosHasta={setPuntosHasta}
              setOrdenPor={setOrdenPor}
              showFiltroRubro={props.showRubroFilter}
            />
          </Box>
        )}

        {items.length > 0 ? (
          <Box display={'flex'} flexWrap={'wrap'} flexDirection={'row'}>
            {items}
          </Box>
        ) : (
          <SinProductos />
        )}

        {items.length > 0 && !props.hidePage && (
          <Text textAlign={'right'} tabIndex={0} py={'16px'}>
            Mostrando {items.length} de {totalFiltered}
          </Text>
        )}

        {items.length > 0 && page < totalPage && (
          <Box justifyContent={'center'}>
            <StyledButton variant={'secondary'} width="fit-content !important" onClick={onClick} onKeyDown={onKeyDown}>
              Ver más
            </StyledButton>
          </Box>
        )}
        {props.legales && props.legales.trim()?.length > 0 && (
          <Box paddingBottom={'40'} paddingTop={'40px'} display={items.length > 0 && props.rubroId ? 'flex' : 'none'}>
            <Text color={'primary.0'} fontWeight={500}>
              <a href={props.legales.trim()}>CONOCÉ LOS TÉRMINOS Y CONDICIONES DE ESTA ACCIÓN ESPECIAL</a>
            </Text>
          </Box>
        )}
      </ModuleContainerGeneric>
    </>
  );
};

CatalogoComponent.defaultProps = {
  prodPorFilaList: [1, 3, 5],
};
